import { useMemo, useState } from 'react'
import * as PickingHttp from '../../../../services/api/https/picking/picking.http'
import { errorHandler } from '../../../../services/util/error.service'
import GrupoProdutoHttp from '../../../../services/api/https/registration/grupo-produto.http'
import {
  getPeriodoMesAtual,
  parseToMysqlFormatWithTime,
} from '../../../../services/util/date.service'

const INIT_STATE = {
  loadingMessage: null,
  errorMessage: null,
  successMessage: null,
  entregas: [],
  entregasOriginais: [],
  selecionarTodos: false,
  selectedCiclosFiltros: [],
  selectedReferenciasFiltros: [],
  selectedTransferFiltros: [],
  transferParaFiltros: [],
  ciclosParaFiltros: [],
  referenciasParaFiltros: [],
  selectedPlacasPrevistas: [],
  placasPrevistasFiltro: [],
  mostrarAssocAutomaticas: false,
  selectedMetodoAssociacao: { value: '' },
  selectedGrupoProduto: { value: '' },
  periodo: getPeriodoMesAtual(),
  modalConfirmGruposDiferentes: {
    show: false,
    notasGruposDiferentes: [],
    selectedGrupoProduto: null,
    products: [],
  },
}
export const METODOS_ASSOCIACAO = [
  { label: '01 Associar NF. por Grupo de Produto', value: 'ANFGP' },
]

export const ASSOC_ENTREGA_LOADING_MESSAGE = {
  FETCH_ENTREGAS: 'fetch_entregas',
  ASSOCIAR_ENTREGAS: 'associar_entregas',
  FILTRAR_PLACAS: 'filtrar_placas',
}

export default function useAssocEntregaState(selectedPicking) {
  const [state, setState] = useState({ ...INIT_STATE })

  const localDispatch = useMemo(() => {
    // -------------------------
    // Private Methods
    // -------------------------

    const getTransferFiltros = entregas => {
      const transferSet = new Set(entregas.map(e => e.transfer_name).filter(Boolean))
      return Array.from(transferSet).map(t => ({ label: t, value: t }))
    }

    const atualizarEntregas = (entregas, successMessage = null) => {
      setState(prev => ({
        ...prev,
        loadingMessage: null,
        entregas,
        entregasOriginais: entregas,
        successMessage,
        ciclosParaFiltros: getCiclosFiltros(entregas),
        referenciasParaFiltros: getReferenciasFiltros(entregas),
        transferParaFiltros: getTransferFiltros(entregas),
      }))
    }

    const showErrorMessage = e => {
      setState(prev => ({ ...prev, loadingMessage: null, errorMessage: errorHandler(e) }))
    }

    const getCiclosFiltros = entregas => {
      const ciclosFiltros = []
      const ciclosIds = new Set(entregas.map(e => e.cycle_id))
      ciclosIds.forEach(id => {
        const ciclo = entregas.find(e => e.cycle_id === id)
        ciclo && ciclosFiltros.push(ciclo)
      })
      return ciclosFiltros.map(cf => ({
        label: `${cf.cycle_id} - ${cf.cycle_name}`,
        value: cf.cycle_id,
      }))
    }

    const handleCicloFiltroChange = (entregas, selectedCiclosIds) => {
      entregas.forEach(e => (e.selected = selectedCiclosIds.some(id => e.cycle_id === id)))
      return [...entregas]
    }

    const getReferenciasFiltros = entregas => {
      const referenciasSet = new Set(entregas.map(e => e.cycle_referencia).filter(Boolean))
      return Array.from(referenciasSet)
        .sort()
        .map(referencia => ({
          label: referencia,
          value: referencia,
        }))
    }

    const handleReferenciaFiltroChange = (entregas, selectedReferencias) => {
      entregas.forEach(e => (e.selected = selectedReferencias.includes(e.cycle_referencia)))
      return [...entregas]
    }

    const handleTransferFiltroChange = (entregas, selectedTransfers) => {
      entregas.forEach(e => (e.selected = selectedTransfers.includes(e.transfer_name)))
      return [...entregas]
    }

    const getPlacasPrevistasFiltro = (entregas, filteredCiclos) => {
      const placasPrevistasFiltro = new Set()

      filteredCiclos.forEach(cycle_id => {
        const entregasSelecionadas = entregas.filter(e => e.cycle_id === cycle_id)
        const hasPlacasPrevistasIgualNull = entregasSelecionadas.some(
          e => !e.placa_prevista_carregamento,
        )
        if (!hasPlacasPrevistasIgualNull) {
          entregasSelecionadas.forEach(e =>
            placasPrevistasFiltro.add(e.placa_prevista_carregamento),
          )
        }
      })

      return placasPrevistasFiltro.size > 0
        ? Array.from(placasPrevistasFiltro).map(p => ({ label: p, value: p }))
        : []
    }

    const hasMultipleGroupsPerIdentifier = products => {
      const identifierMap = new Map()
      const notasGruposDiferentes = new Set()
      products.forEach(({ identifier, grupo_produto_id }) => {
        if (!identifierMap.has(identifier)) {
          identifierMap.set(identifier, new Set())
        }
        identifierMap.get(identifier).add(grupo_produto_id)
      })

      for (const [identifier, groupSet] of identifierMap.entries()) {
        if (groupSet.size > 1) {
          notasGruposDiferentes.add(identifier)
        }
      }

      return Array.from(notasGruposDiferentes)
    }

    // ---------------------
    // Public Methods
    // ---------------------
    return {
      getDeliveries(dataInicial, dataFinal, statusIds) {
        PickingHttp.getDeliveriesForPickings(
          selectedPicking.id,
          parseToMysqlFormatWithTime(dataInicial),
          parseToMysqlFormatWithTime(dataFinal),
          statusIds,
        )
          .then(atualizarEntregas)
          .catch(showErrorMessage)
      },

      resetMessage() {
        setState(prev => ({ ...prev, errorMessage: null, successMessage: null }))
      },

      salvarAssociacaoDasEntregas(entregas) {
        const selectedEntregasId = entregas.filter(e => e.selected).map(i => i.delivery_id)
        PickingHttp.associateDeliveryToPicking(selectedEntregasId, selectedPicking.id)
          .then(deliveries =>
            atualizarEntregas(deliveries, 'Associação/Desassociação realizada com sucesso !'),
          )
          .catch(showErrorMessage)
      },

      filtrarPorPlacaPrevista() {
        setState(prev => {
          const { entregasOriginais, selectedPlacasPrevistas, selectedCiclosFiltros } = prev

          const unSelectedEntregas = entregasOriginais.map(e => ({ ...e, selected: false }))

          let selectedEntregasByCiclo = []
          selectedCiclosFiltros.forEach(cycle_id => {
            const entregasCiclo = unSelectedEntregas.filter(e => e.cycle_id === cycle_id)
            selectedEntregasByCiclo = [...selectedEntregasByCiclo, ...entregasCiclo]
          })

          let selectedEntregasByCicloByPlaca = []
          selectedPlacasPrevistas.forEach(placa => {
            const entregasPlaca = selectedEntregasByCiclo.filter(
              e => e.placa_prevista_carregamento === placa,
            )
            selectedEntregasByCicloByPlaca = [...selectedEntregasByCicloByPlaca, ...entregasPlaca]
          })

          return {
            ...prev,
            loadingMessage: null,
            entregas: selectedEntregasByCicloByPlaca.map(e => ({ ...e, selected: true })),
          }
        })
      },

      // Events
      onLoadingMessageChange(message, payload = null) {
        switch (message) {
          case ASSOC_ENTREGA_LOADING_MESSAGE.FETCH_ENTREGAS:
            setState(prev => ({ ...prev, loadingMessage: message }))
            break
          case ASSOC_ENTREGA_LOADING_MESSAGE.ASSOCIAR_ENTREGAS:
            setState(prev => ({ ...prev, loadingMessage: message }))
            break
          case ASSOC_ENTREGA_LOADING_MESSAGE.FILTRAR_PLACAS:
            setState(prev => ({
              ...prev,
              loadingMessage: message,
              selectedPlacasPrevistas: payload,
            }))
            break
          default:
            setState(prev => ({ ...prev, loadingMessage: null }))
            break
        }
      },

      onBeepNfSelect(delivery_id) {
        setState(prev => {
          const entregas = prev.entregas.map(e =>
            e.delivery_id === delivery_id ? { ...e, selected: true } : e,
          )
          return {
            ...prev,
            entregas,
            selecionarTodos: entregas.length > 0 && entregas.every(e => e.selected),
          }
        })
      },

      onCheckBoxRowClicked(entrega) {
        entrega.selected = !entrega.selected
        setState(prev => {
          const index = prev.entregas.findIndex(e => e.delivery_id === entrega.delivery_id)
          if (index !== -1) {
            prev.entregas[index] = entrega
          }
          return { ...prev }
        })
      },

      onSelecionarTodosChange(value, visibleIds) {
        setState(prev => {
          const idsVisiveis = new Set(visibleIds)
          return {
            ...prev,
            entregas: prev.entregas.map(e =>
              idsVisiveis.has(e.delivery_id) ? { ...e, selected: value } : e,
            ),
            selecionarTodos: value,
          }
        })
      },

      onCicloFiltroChange(filteredCiclos) {
        setState(prev => {
          const placasPrevistas = getPlacasPrevistasFiltro(prev.entregas, filteredCiclos)

          return {
            ...prev,
            selectedCiclosFiltros: filteredCiclos,
            entregas: handleCicloFiltroChange(prev.entregasOriginais, filteredCiclos),
            placasPrevistasFiltro: placasPrevistas,
            selectedPlacasPrevistas: placasPrevistas.map(p => p.value),
          }
        })
      },

      onReferenciaFiltroChange(filteredReferencias) {
        setState(prev => ({
          ...prev,
          selectedReferenciasFiltros: filteredReferencias,
          entregas: handleReferenciaFiltroChange(prev.entregasOriginais, filteredReferencias),
        }))
      },

      onTransferFiltroChange(filteredTransfers) {
        setState(prev => ({
          ...prev,
          selectedTransferFiltros: filteredTransfers,
          entregas: handleTransferFiltroChange(prev.entregasOriginais, filteredTransfers),
        }))
      },

      onMostrarAssocAutomaticasChange(value) {
        setState(prev => ({
          ...prev,
          mostrarAssocAutomaticas: value,
          selectedMetodoAssociacao: '',
        }))
      },

      async onMetodoAssociacaoChange(metodo) {
        setState(prevState => ({ ...prevState, selectedMetodoAssociacao: metodo }))
        switch (metodo.value) {
          case 'ANFGP':
            const _grupos = (await GrupoProdutoHttp.getGrupos(selectedPicking.company_id)).map(
              i => ({
                label: i.nome,
                value: i.id,
              }),
            )
            setState(prevState => ({
              ...prevState,
              grupoProdutos: _grupos,
              selectedGrupoProduto: _grupos[0],
            }))
            break

          default:
            break
        }
      },

      async onBotaoAssociarClick(selectedGrupoProduto) {
        try {
          const products = await PickingHttp.getDeliveriesProductsForPickings(selectedPicking.id)

          const grupos = new Set(products.map(p => p.grupo_produto_id || 0))
          if (grupos.has(0)) {
            const produtosSemGrupo = products
              .filter(p => !p.grupo_produto_id)
              .map(p => p.product_code)
            showErrorMessage(
              `Existem produtos sem grupo de produto associado: ${produtosSemGrupo.join(', ')}`,
            )
            return
          }

          const notasGruposDiferentes = hasMultipleGroupsPerIdentifier(products)
          if (notasGruposDiferentes.length > 0) {
            setState(prevState => ({
              ...prevState,
              modalConfirmGruposDiferentes: {
                show: true,
                notasGruposDiferentes: notasGruposDiferentes,
                selectedGrupoProduto: selectedGrupoProduto,
                products: products,
              },
            }))
            return
          }

          const entregasSelecionadas = products
            .filter(p => p.grupo_produto_id === selectedGrupoProduto.value)
            .map(p => p.identifier)

          setState(prevState => {
            const entregas = prevState.entregas.filter(e =>
              entregasSelecionadas.includes(e.identifier),
            )
            if (!entregas.length) {
              return {
                ...prevState,
                loadingMessage: null,
                errorMessage: 'Nenhuma entrega encontrada para o grupo selecionado',
              }
            }
            return {
              ...prevState,
              entregas: entregas.map(e => ({ ...e, selected: true })),
              loadingMessage: ASSOC_ENTREGA_LOADING_MESSAGE.ASSOCIAR_ENTREGAS,
            }
          })
        } catch (e) {
          showErrorMessage(e)
        }
      },

      onGrupoProdutoChange(grupo) {
        setState(prevState => ({ ...prevState, selectedGrupoProduto: grupo }))
      },

      onPeriodoChange(periodo) {
        setState(prevState => ({ ...prevState, periodo }))
      },

      onModalConfirmGruposDiferentesClose() {
        setState(prevState => ({
          ...prevState,
          modalConfirmGruposDiferentes: {
            show: false,
            notasGruposDiferentes: [],
            selectedGrupoProduto: null,
            products: [],
          },
        }))
      },

      onModalConfirmGruposDiferentesConfirm() {
        setState(prevState => {
          const { modalConfirmGruposDiferentes } = prevState
          const { selectedGrupoProduto, products, notasGruposDiferentes } =
            modalConfirmGruposDiferentes

          const produtosFiltrados = products.filter(
            product => !notasGruposDiferentes.includes(product.identifier),
          )

          const entregasSelecionadas = produtosFiltrados
            .filter(p => p.grupo_produto_id === selectedGrupoProduto.value)
            .map(p => p.identifier)

          const entregas = prevState.entregas.filter(e =>
            entregasSelecionadas.includes(e.identifier),
          )
          if (!entregas.length) {
            return {
              ...prevState,
              modalConfirmGruposDiferentes: {
                show: false,
                notasGruposDiferentes: [],
                selectedGrupoProduto: null,
                products: [],
              },
              loadingMessage: null,
              errorMessage: 'Nenhuma entrega encontrada para o grupo selecionado',
            }
          }
          return {
            ...prevState,
            modalConfirmGruposDiferentes: {
              show: false,
              notasGruposDiferentes: [],
              selectedGrupoProduto: null,
              products: [],
            },
            entregas: entregas.map(e => ({ ...e, selected: true })),
            loadingMessage: ASSOC_ENTREGA_LOADING_MESSAGE.ASSOCIAR_ENTREGAS,
          }
        })
      },
    }
  }, [])

  return { state, localDispatch }
}
